import React, { useState, useEffect } from 'react';
import { tasksApi, usersApi, projectsApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { GanttChart, Layers, Filter } from 'lucide-react';
import { toast } from 'sonner';
import GanttTimeline from '../components/GanttTimeline';

const Timeline = () => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tasksRes, usersRes, projRes] = await Promise.all([
          tasksApi.getAll(),
          usersApi.getAll(),
          projectsApi.getAll({}),
        ]);
        setTasks(tasksRes.data);
        setUsers(usersRes.data);
        setProjects(projRes.data);
      } catch {
        toast.error('Failed to load timeline data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter tasks
  const filtered = tasks.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (projectFilter === 'standalone') return !t.project_id;
    if (projectFilter !== 'all') return t.project_id === projectFilter;
    return true;
  });

  // Only show tasks with at least one date
  const datedTasks = filtered.filter(t => t.start_date || t.due_date);

  // Stats
  const stats = {
    total: filtered.length,
    dated: datedTasks.length,
    overdue: datedTasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'done').length,
    onTrack: datedTasks.filter(t => t.due_date && new Date(t.due_date) >= new Date() && t.status !== 'done').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timeline-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Timeline</h1>
          <p className="text-navy/60 mt-1">Visualize task schedules across all projects</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-navy/10">
          <CardContent className="p-5">
            <p className="text-sm text-navy/60">Total Tasks</p>
            <p className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth'] mt-1">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-navy/10">
          <CardContent className="p-5">
            <p className="text-sm text-navy/60">On Timeline</p>
            <p className="text-3xl font-bold text-[#106EFD] font-['Amaranth'] mt-1">{stats.dated}</p>
          </CardContent>
        </Card>
        <Card className="border-navy/10">
          <CardContent className="p-5">
            <p className="text-sm text-navy/60">On Track</p>
            <p className="text-3xl font-bold text-[#37C563] font-['Amaranth'] mt-1">{stats.onTrack}</p>
          </CardContent>
        </Card>
        <Card className="border-navy/10">
          <CardContent className="p-5">
            <p className="text-sm text-navy/60">Overdue</p>
            <p className="text-3xl font-bold text-red-500 font-['Amaranth'] mt-1">{stats.overdue}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex items-center gap-2 text-navy/50">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium">Filters:</span>
        </div>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-[220px]" data-testid="timeline-project-filter">
            <Layers className="w-4 h-4 mr-2 text-navy/40" />
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            <SelectItem value="standalone">Standalone Tasks</SelectItem>
            {projects.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[170px]" data-testid="timeline-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="todo">To Do</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="review">In Review</SelectItem>
            <SelectItem value="done">Complete</SelectItem>
          </SelectContent>
        </Select>
        {(projectFilter !== 'all' || statusFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setProjectFilter('all'); setStatusFilter('all'); }}
            className="text-navy/50 hover:text-navy"
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Gantt Chart */}
      <GanttTimeline
        tasks={datedTasks}
        users={users}
        projects={projects}
        groupByProject={projectFilter === 'all'}
      />

      {/* Undated tasks notice */}
      {filtered.length > datedTasks.length && (
        <div className="text-sm text-navy/40 text-center py-2" data-testid="undated-notice">
          {filtered.length - datedTasks.length} task(s) without dates are not shown on the timeline
        </div>
      )}
    </div>
  );
};

export default Timeline;
